import { useMemo } from "react";
import dayjs from "dayjs";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ErrorTimeChart from "./ErrorTimeChart";
import { formatTimestamp, truncate } from "./formatters";
import { LogEntry, LogMetadata } from "./types";
import { tokens, EMPTY, SEVERITY, severityForCode } from "./tokens";

interface ErrorCodeAccordionsProps {
  logs: LogEntry[];
  bucketMinutes: number;
}

const isHttp = (c: string) => /^\d{3}$/.test(c);

// Derive a small fixed-width volume sparkline from a group's timestamps:
// split the [min, max] window into N even buckets and count entries per bucket.
function sparklineBuckets(entries: LogEntry[], n = 7): number[] {
  const times = entries
    .map((e) => (e.timestamp ? dayjs(e.timestamp).valueOf() : NaN))
    .filter((t) => !Number.isNaN(t));
  if (!times.length) return [];
  const min = Math.min(...times);
  const max = Math.max(...times);
  const buckets = new Array(n).fill(0);
  const span = max - min;
  for (const t of times) {
    // When all timestamps collapse to one instant, drop everything in bucket 0.
    const idx = span === 0 ? 0 : Math.min(n - 1, Math.floor(((t - min) / span) * n));
    buckets[idx] += 1;
  }
  return buckets;
}

function lastSeen(entries: LogEntry[]): string {
  let max = -Infinity;
  for (const e of entries) {
    if (!e.timestamp) continue;
    const v = dayjs(e.timestamp).valueOf();
    if (!Number.isNaN(v) && v > max) max = v;
  }
  return max === -Infinity ? EMPTY : dayjs(max).format("HH:mm");
}

interface SparklineProps {
  buckets: number[];
  color: string;
}

function Sparkline({ buckets, color }: SparklineProps) {
  if (!buckets.length) return null;
  const peak = Math.max(1, ...buckets);
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-end",
        gap: "2px",
        height: 18,
        ml: 0.5,
      }}
      aria-hidden
    >
      {buckets.map((v, i) => (
        <Box
          key={i}
          sx={{
            width: 3,
            borderRadius: "1px",
            bgcolor: color,
            // Keep a faint stub for empty buckets so the bar count stays readable.
            opacity: v === 0 ? 0.18 : 0.85,
            height: `${Math.max(2, Math.round((v / peak) * 18))}px`,
          }}
        />
      ))}
    </Box>
  );
}

export default function ErrorCodeAccordions({ logs, bucketMinutes }: ErrorCodeAccordionsProps) {
  const grouped = useMemo(() => {
    const map: Record<string, LogEntry[]> = {};
    for (const log of logs || []) {
      const code = log.metadata?.statusCode ?? log.metadata?.errorCode;
      if (code == null) continue;
      const key = String(code);
      if (!map[key]) map[key] = [];
      map[key].push(log);
    }
    return Object.entries(map)
      .sort(([, a], [, b]) => b.length - a.length)
      .map(([code, entries]) => ({ code, entries }));
  }, [logs]);

  if (!grouped.length) return null;

  const copyRequestId = (id: string) => {
    try {
      navigator.clipboard?.writeText(id);
    } catch {
      /* clipboard unavailable — non-fatal */
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1, color: tokens.textSecondary }}>
        Error Breakdown
      </Typography>
      {grouped.map(({ code, entries }) => {
        const sev = SEVERITY[severityForCode(code)];
        const buckets = sparklineBuckets(entries);
        return (
          <Accordion
            key={code}
            disableGutters
            elevation={0}
            sx={{
              bgcolor: tokens.surface,
              border: `1px solid ${tokens.divider}`,
              borderRadius: "6px",
              mb: 0.75,
              "&:before": { display: "none" },
              "&.Mui-expanded": { mb: 0.75 },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ fontSize: 18, color: tokens.textSecondary }} />}
              sx={{
                px: 1.5,
                "& .MuiAccordionSummary-content": {
                  alignItems: "center",
                  gap: 1.25,
                  my: 0.75,
                },
              }}
            >
              {/* (1) status pill */}
              <Chip
                label={isHttp(code) ? `HTTP ${code}` : code}
                size="small"
                sx={{
                  bgcolor: sev.bg,
                  color: sev.text,
                  fontWeight: 700,
                  fontFamily: tokens.mono,
                  fontSize: "0.7rem",
                }}
              />

              {/* (2) occurrence count */}
              <Typography variant="body2" sx={{ fontSize: "0.78rem" }}>
                <Box component="span" sx={{ fontWeight: 700, color: tokens.textPrimary }}>
                  {entries.length}
                </Box>{" "}
                <Box component="span" sx={{ color: tokens.textSecondary }}>
                  occurrence{entries.length !== 1 ? "s" : ""}
                </Box>
              </Typography>

              {/* (3) volume sparkline */}
              <Sparkline buckets={buckets} color={sev.text} />

              {/* (4) last seen, right-aligned */}
              <Typography
                variant="caption"
                sx={{ ml: "auto", color: tokens.textSecondary, fontSize: "0.7rem", whiteSpace: "nowrap" }}
              >
                last seen {lastSeen(entries)}
              </Typography>
            </AccordionSummary>

            <AccordionDetails sx={{ pt: 0, px: 2 }}>
              <ErrorTimeChart logs={entries} bucketMinutes={bucketMinutes} color={sev.text} />

              {/* Detail table for debugging */}
              <Box
                component="table"
                sx={{
                  width: "100%",
                  borderCollapse: "collapse",
                  tableLayout: "fixed",
                  mt: 1.5,
                  fontSize: "0.75rem",
                  "& th": {
                    textAlign: "left",
                    color: tokens.textSecondary,
                    fontWeight: 600,
                    fontSize: "0.68rem",
                    pb: 0.5,
                    borderBottom: `1px solid ${tokens.divider}`,
                    textTransform: "uppercase",
                    letterSpacing: "0.03em",
                  },
                  "& td": {
                    py: 0.75,
                    pr: 1,
                    borderBottom: `1px solid ${tokens.divider}`,
                    verticalAlign: "top",
                    color: tokens.textPrimary,
                  },
                  "& tr:last-child td": { borderBottom: 0 },
                }}
              >
                <thead>
                  <tr>
                    <th style={{ width: "26%" }}>Time</th>
                    <th style={{ width: "34%" }}>Origin API</th>
                    <th style={{ width: "40%" }}>Message</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.slice(0, 15).map((log, idx) => {
                    const m: LogMetadata = log.metadata || {};
                    const endpoint = m.url || m.endpoint;
                    const supplier = m.supplier ?? log.supplier;
                    const segments = m.originApi
                      ? String(m.originApi)
                          .split(" / ")
                          .map((s) => s.trim())
                          .filter(Boolean)
                      : [];
                    const requestId =
                      m.requestId && m.requestId !== "unknown" ? String(m.requestId) : "";
                    return (
                      <tr key={log.id || idx}>
                        {/* Time */}
                        <td
                          style={{
                            fontFamily: tokens.mono,
                            fontSize: "0.72rem",
                            color: tokens.textSecondary,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {formatTimestamp(log.timestamp)}
                        </td>

                        {/* Origin API breadcrumb + endpoint + supplier */}
                        <td>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 }}>
                            {segments.length > 0 ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  flexWrap: "wrap",
                                  rowGap: 0.25,
                                  color: tokens.textSecondary,
                                  fontSize: "0.72rem",
                                }}
                              >
                                {segments.map((seg, i) => (
                                  <Box
                                    component="span"
                                    key={i}
                                    sx={{ display: "inline-flex", alignItems: "center" }}
                                  >
                                    {i > 0 && (
                                      <Box
                                        component="span"
                                        sx={{ mx: 0.5, color: tokens.textTertiary }}
                                      >
                                        ›
                                      </Box>
                                    )}
                                    <Box component="span">{seg}</Box>
                                  </Box>
                                ))}
                              </Box>
                            ) : null}

                            {endpoint && (
                              <Tooltip title={String(endpoint)} placement="top-start">
                                <Box
                                  component="span"
                                  sx={{
                                    fontFamily: tokens.mono,
                                    fontSize: "0.68rem",
                                    color: tokens.textTertiary,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {String(endpoint)}
                                </Box>
                              </Tooltip>
                            )}

                            {supplier && (
                              <Box
                                component="span"
                                sx={{
                                  fontSize: "0.68rem",
                                  color: tokens.textTertiary,
                                  textTransform: "capitalize",
                                }}
                              >
                                supplier: {String(supplier)}
                              </Box>
                            )}

                            {segments.length === 0 && !endpoint && !supplier && (
                              <Box component="span" sx={{ color: tokens.textTertiary }}>
                                {EMPTY}
                              </Box>
                            )}
                          </Box>
                        </td>

                        {/* Message + requestId with copy */}
                        <td style={{ fontSize: "0.72rem" }}>
                          <Tooltip
                            title={log.message && log.message.length > 100 ? log.message : ""}
                            placement="top-start"
                          >
                            <Box component="span">{truncate(log.message, 100)}</Box>
                          </Tooltip>
                          {requestId && (
                            <Box sx={{ mt: 0.25, display: "flex", alignItems: "center", gap: 0.25, minWidth: 0 }}>
                              <Tooltip title={requestId} placement="top-start">
                                <Box
                                  component="span"
                                  sx={{
                                    fontFamily: tokens.mono,
                                    fontSize: "0.65rem",
                                    color: tokens.textTertiary,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  req: {requestId}
                                </Box>
                              </Tooltip>
                              <Tooltip title="Copy request ID">
                                <IconButton
                                  size="small"
                                  onClick={() => copyRequestId(requestId)}
                                  sx={{ p: 0.25, color: tokens.textTertiary, flexShrink: 0 }}
                                >
                                  <ContentCopyIcon sx={{ fontSize: 13 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Box>
              {entries.length > 15 && (
                <Typography variant="caption" sx={{ display: "block", mt: 1, color: tokens.textSecondary }}>
                  + {entries.length - 15} more entries
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}
